import { Logger, ConsoleTransport, type Transport } from '@axiomhq/logging';
import { auth } from './firebase';

/**
 * Custom transport that sends logs to our backend with Firebase auth.
 * Implements the Axiom Transport interface directly since ProxyTransport
 * doesn't support dynamic headers.
 */
class AuthenticatedProxyTransport implements Transport {
  private static readonly CHUNK_SIZE = 90; // Safety margin below backend's 100 limit
  private static readonly FLUSH_THRESHOLD = 50; // Flush early when buffer gets large
  private url: string;
  private buffer: unknown[] = [];
  private timer: ReturnType<typeof setTimeout> | null = null;
  private flushIntervalMs: number;
  private isFlushing = false;

  constructor(url: string, options: { durationMs: number }) {
    this.url = url;
    this.flushIntervalMs = options.durationMs;
  }

  log = (logs: unknown[]): void => {
    this.buffer.push(...logs);
    if (this.buffer.length >= AuthenticatedProxyTransport.FLUSH_THRESHOLD) {
      this.flush(); // Immediate flush when threshold reached
    } else {
      this.scheduleFlush(); // Normal timer-based flush
    }
  };

  private scheduleFlush(): void {
    if (this.timer) return;
    this.timer = setTimeout(() => {
      this.timer = null;
      this.flush();
    }, this.flushIntervalMs);
  }

  async flush(): Promise<void> {
    // Prevent concurrent flushes - schedule a follow-up if needed
    if (this.isFlushing) {
      if (!this.timer) {
        this.scheduleFlush();
      }
      return;
    }

    if (this.buffer.length === 0) return;
    this.isFlushing = true;

    try {
      // Take a snapshot of current buffer and clear it atomically
      const logs = this.buffer.splice(0, this.buffer.length);

      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };

      // Add Firebase auth if available
      const user = auth?.currentUser;
      if (user) {
        try {
          const token = await user.getIdToken();
          headers['Authorization'] = `Bearer ${token}`;
        } catch {
          // Auth failed, still try to send (backend may allow some logs)
        }
      }

      // Split into chunks to stay under backend's 100 entry limit
      const chunks: unknown[][] = [];
      for (let i = 0; i < logs.length; i += AuthenticatedProxyTransport.CHUNK_SIZE) {
        chunks.push(logs.slice(i, i + AuthenticatedProxyTransport.CHUNK_SIZE));
      }

      const failures: unknown[] = [];
      for (const chunk of chunks) {
        try {
          const response = await fetch(this.url, {
            method: 'POST',
            headers,
            body: JSON.stringify(chunk),
          });
          // Retry only on rate limits (429) or server errors (5xx)
          // Don't retry 400/401/403 - those are permanent failures
          if (response.status === 429 || response.status >= 500) {
            failures.push(...chunk);
          }
        } catch {
          // Network error - retry
          failures.push(...chunk);
        }
      }

      // Re-queue only failed chunks
      if (failures.length > 0) {
        this.buffer.unshift(...failures);
        this.scheduleFlush();
      }
    } finally {
      this.isFlushing = false;
      // If new logs arrived during flush, check if immediate or scheduled flush needed
      if (this.buffer.length > 0 && !this.timer) {
        if (this.buffer.length >= AuthenticatedProxyTransport.FLUSH_THRESHOLD) {
          this.flush(); // Immediate flush if buffer is large
        } else {
          this.scheduleFlush();
        }
      }
    }
  }
}

const transports: [Transport, ...Transport[]] = [
  new AuthenticatedProxyTransport(
    `${import.meta.env.VITE_API_URL || '/api'}/logs`,
    { durationMs: 5000 }
  ),
];

// Add console transport in development
if (import.meta.env.DEV) {
  transports.push(new ConsoleTransport({ prettyPrint: true }));
}

export const axiom = new Logger({ transports });
